import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import DeliveryDiningIcon from "@mui/icons-material/DeliveryDining";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import FoodBankIcon from "@mui/icons-material/FoodBank";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LocalLibraryOutlinedIcon from "@mui/icons-material/LocalLibraryOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import { Stack, Typography, alpha } from "@mui/material";
import Link from "next/link";
import { useMemo } from "react";

import SafeImage from "@/components/common/SafeImage";
import { ORDER_TYPE_ENUM, STORE_STATUS_ENUM } from "@/constants/enums";
import { APP_STORE_BADGE, PLAY_STORE_BADGE } from "@/constants/images";
import { ROUTE_MENU } from "@/constants/routes";
import {
  STATUS_BREAK,
  STATUS_CLOSED,
  STATUS_OPEN,
  STATUS_UNKNOWN,
} from "@/constants/colors";
import useIsWebView from "@/hooks/useIsWebView";
import useStoreStatus from "@/hooks/useStoreStatus";
// ADDITION — branch (outlet) selection; see src/lib/branch.ts.
import { useBranch } from "@/context/BranchContext";
import { useAppDispatch, useAppSelector } from "@/store";
import { openDrawer, openModal } from "@/store/slices/commonSlice";
import { DRAWER_VIEW, MODAL_VIEW } from "@/constants/views";
import StoreRating from "@/components/menu/StoreRating";
import { formatDuration } from "@/utils/format";

import {
  ArrowCircle,
  BusinessName,
  CardBadge,
  CardContent,
  CardEta,
  CardSubtitle,
  CardText,
  CardTitle,
  ChipRow,
  FooterSection,
  FooterText,
  HeaderSection,
  IconCircle,
  LandingCard as LandingCardRoot,
  LogoImage,
  LogoInitial,
  LogoWrapper,
  OrderSection,
  OrderTypeCard,
  OrderTypeList,
  SectionDivider,
  SectionLabel,
  StatusChip,
  StatusDot,
  StoreBadgeLink,
  Tagline,
  WelcomeText,
} from "./styles";

const linkStyle = { textDecoration: "none", width: "100%", outline: "none" };

export default function LandingCard() {
  const dispatch = useAppDispatch();
  const {
    web_enable_takeaway: takeawayEnabled,
    web_enable_delivery: deliveryEnabled,
    takeaway_is_pickup: takeawayIsPickup,
    public_business_name: businessName,
    image_url: logoUrl,
    playstore_url: playStoreUrl,
    appstore_url: appStoreUrl,
    delivery_time: deliveryTime,
    takeaway_time: takeawayTime,
    reservation_enabled: reservationEnabled,
  } = useAppSelector((state) => state.settings.data);

  const storeStatus = useStoreStatus();
  // ADDITION. `branches.length < 2` on a single-outlet brand, and on any build
  // whose API has no branch directory, so the chip below simply does not render
  // and the card is byte-for-byte what it was.
  const { branches, current: currentBranch, openPicker } = useBranch();
  const branchLabel =
    currentBranch?.city?.trim() || currentBranch?.name || businessName || "";


  const orderingAvailable =
    takeawayEnabled || deliveryEnabled || reservationEnabled;


  const status = useMemo(() => {
    const values = Object.values(storeStatus);
    if (values.length === 0)
      return { label: "Checking hours", color: STATUS_UNKNOWN };
    if (values.some((entry) => entry?.status === STORE_STATUS_ENUM.OPEN)) {
      return { label: "Open now", color: STATUS_OPEN };
    }
    if (values.some((entry) => entry?.status === STORE_STATUS_ENUM.BREAK)) {
      return { label: "On break", color: STATUS_BREAK };
    }
    return { label: "Closed", color: STATUS_CLOSED };
  }, [storeStatus]);

  const deliveryEta = formatDuration(deliveryTime);
  const takeawayEta = formatDuration(takeawayTime);

  const isWebView = useIsWebView();
  const hasPlayStore = Boolean(playStoreUrl);
  const hasAppStore = Boolean(appStoreUrl);
  const hasAnyStoreLink = hasPlayStore || hasAppStore;

  const initial = (businessName?.trim()?.[0] ?? "R").toUpperCase();

  return (
    <LandingCardRoot
      direction="column"
      alignItems="center"
      justifyContent="space-between"
    >
      <HeaderSection>
        <LogoWrapper>
          {logoUrl ? (
            <LogoImage
              src={logoUrl}
              alt={`${businessName} logo`}
              placeholder="logo"
            />
          ) : (
            <LogoInitial variant="h3" fontWeight={700}>
              {initial}
            </LogoInitial>
          )}
        </LogoWrapper>

        <WelcomeText variant="body2">Welcome to</WelcomeText>

        <BusinessName component="h1" variant="h4" fontWeight={800}>
          {businessName || "Our Restaurant"}
        </BusinessName>

        <Tagline variant="body1">
          {orderingAvailable
            ? "Choose your preferred order type to get started"
            : "Ordering is currently unavailable"}
        </Tagline>

        <ChipRow direction="row" spacing={1}>
          {/* ADDITION — the branch chip. Reuses the StatusChip shell beside it
              rather than introducing a new one, so the picker entry point does
              not look bolted onto a recovered layout. */}
          {branches.length > 1 && (
            <StatusChip
              type="button"
              onClick={openPicker}
              aria-label="Change branch"
            >
              <StorefrontOutlinedIcon sx={{ fontSize: 14, opacity: 0.7 }} />
              <Typography variant="caption" fontWeight={600}>
                {branchLabel}
              </Typography>
              <ExpandMoreIcon sx={{ fontSize: 14, opacity: 0.7 }} />
            </StatusChip>
          )}

          <StatusChip
            type="button"
            onClick={() => dispatch(openDrawer(DRAWER_VIEW.STORE_SCHEDULE))}
          >
            <StatusDot style={{ backgroundColor: status.color }} />
            <Typography variant="caption" fontWeight={600}>
              {status.label}
            </Typography>
            <AccessTimeIcon sx={{ fontSize: 14, opacity: 0.7 }} />
          </StatusChip>

          <StoreRating />
        </ChipRow>
      </HeaderSection>

      {/* No announcement here: the original surfaces `announcement_message` as
          a dialog on the MENU page (see AnnouncementDialog), never on the
          landing card, whose children are exactly header / order types /
          footer. */}
      {orderingAvailable && (
        <OrderSection>
          <SectionDivider>
            <SectionLabel variant="caption">
              How would you like to order?
            </SectionLabel>
          </SectionDivider>

          <OrderTypeList>
            {takeawayEnabled && (
              <Link
                prefetch={false}
                href={`${ROUTE_MENU}?order_type=${ORDER_TYPE_ENUM.TAKE_AWAY.toLowerCase()}`}
                style={linkStyle}
              >
                <OrderTypeCard className="order-type-card takeaway-card">
                  <CardContent className="card-content">
                    <IconCircle
                      className="icon-circle"
                      sx={{ backgroundColor: "primary.contrastText" }}
                    >
                      <FoodBankIcon
                        sx={{ fontSize: 40, color: "primary.main" }}
                      />
                    </IconCircle>
                    <CardText>
                      <CardTitle component="h2" variant="h5" fontWeight={700}>
                        {takeawayIsPickup ? "Pickup" : "Takeaway"}
                      </CardTitle>
                      <CardSubtitle variant="body2">
                        Order &amp; pick up at your convenience
                      </CardSubtitle>
                      {takeawayEta && (
                        <CardEta>
                          <AccessTimeIcon sx={{ fontSize: 13 }} />
                          {takeawayEta}
                        </CardEta>
                      )}
                    </CardText>
                    <ArrowCircle className="arrow-circle">
                      <ArrowForwardIcon sx={{ fontSize: 24 }} />
                    </ArrowCircle>
                  </CardContent>
                </OrderTypeCard>
              </Link>
            )}

            {deliveryEnabled && (
              <Link
                prefetch={false}
                href={`${ROUTE_MENU}?order_type=${ORDER_TYPE_ENUM.DELIVERY.toLowerCase()}`}
                style={linkStyle}
              >
                <OrderTypeCard className="order-type-card delivery-card">
                  <CardContent className="card-content">
                    <IconCircle
                      className="icon-circle"
                      sx={{ backgroundColor: "primary.main" }}
                    >
                      <DeliveryDiningIcon
                        sx={{ fontSize: 40, color: "primary.contrastText" }}
                      />
                    </IconCircle>
                    <CardText>
                      <CardTitle component="h2" variant="h5" fontWeight={700}>
                        Delivery
                      </CardTitle>
                      <CardSubtitle variant="body2">
                        We&apos;ll deliver fresh to your doorstep
                      </CardSubtitle>
                      {deliveryEta && (
                        <CardEta>
                          <AccessTimeIcon sx={{ fontSize: 13 }} />
                          {deliveryEta}
                        </CardEta>
                      )}
                    </CardText>
                    <ArrowCircle className="arrow-circle">
                      <ArrowForwardIcon sx={{ fontSize: 24 }} />
                    </ArrowCircle>
                  </CardContent>
                </OrderTypeCard>
              </Link>
            )}

            {reservationEnabled && (
              <OrderTypeCard
                className="order-type-card reservation-card"
                onClick={() => dispatch(openModal(MODAL_VIEW.RESERVATION))}
                sx={{ cursor: "pointer" }}
              >
                <CardContent className="card-content">
                  <IconCircle
                    className="icon-circle"
                    sx={{
                      backgroundColor: "primary.main",
                      border: "none",
                      boxShadow: (theme) =>
                        `0 10px 20px ${alpha(theme.palette.primary.main, 0.35)}`,
                    }}
                  >
                    <LocalLibraryOutlinedIcon
                      sx={{ fontSize: 40, color: "primary.contrastText" }}
                    />
                  </IconCircle>
                  <CardText>
                    <CardBadge variant="caption">Table Booking</CardBadge>
                    <CardTitle component="h2" variant="h5" fontWeight={700}>
                      Reservation
                    </CardTitle>
                    <CardSubtitle variant="body2">
                      Reserve your seat for dine-in service
                    </CardSubtitle>
                  </CardText>
                  <ArrowCircle
                    className="arrow-circle"
                    sx={{
                      backgroundColor: (theme) =>
                        alpha(theme.palette.primary.main, 0.18),
                      color: "primary.main",
                    }}
                  >
                    <EventAvailableOutlinedIcon sx={{ fontSize: 22 }} />
                  </ArrowCircle>
                </CardContent>
              </OrderTypeCard>
            )}
          </OrderTypeList>
        </OrderSection>
      )}

      {/* No quick-link row here: the live original's landing carries only the
          status chip and the two order-type cards (verified at desktop AND
          375px). Every destination that row pointed at — About us, Reviews,
          Delivery information, Recent orders — is already in the menu page's
          "More" menu (components/common/AppMenu.tsx), so nothing is lost. */}
      <FooterSection>
        {isWebView || !hasAnyStoreLink ? (
          <FooterText variant="body2">
            Quick, easy, and delicious • Order in just a few taps
          </FooterText>
        ) : (
          <Stack direction="column" alignItems="center" spacing={1.5}>
            <FooterText variant="body2" fontWeight={500}>
              Download our mobile app
            </FooterText>
            <Stack direction="row" spacing={1.5}>
              {hasPlayStore && (
                <StoreBadgeLink
                  href={playStoreUrl ?? ""}
                  target="_blank"
                  rel="noreferrer"
                >
                  <SafeImage
                    src={PLAY_STORE_BADGE}
                    alt="Get it on Google Play"
                    placeholder="generic"
                    width={120}
                    height={40}
                  />
                </StoreBadgeLink>
              )}
              {hasAppStore && (
                <StoreBadgeLink
                  href={appStoreUrl ?? ""}
                  target="_blank"
                  rel="noreferrer"
                >
                  <SafeImage
                    src={APP_STORE_BADGE}
                    alt="Download on App Store"
                    placeholder="generic"
                    width={120}
                    height={40}
                  />
                </StoreBadgeLink>
              )}
            </Stack>
          </Stack>
        )}
      </FooterSection>
    </LandingCardRoot>
  );
}
